import type { PropertyValues } from 'lit'
import type Tooltip from '../tooltip/Tooltip.js'
import type { TimestampFormat } from './_timestamp-helpers.js'
import { html, LitElement, nothing } from 'lit'
import { customElement, property } from 'lit/decorators.js'
import { createRef, ref } from 'lit/directives/ref.js'

import { injectLightDomStyles, setLightDomScope } from '../common/light-dom-styles.js'
import { DraftComponentMixin } from '../common/mixins/DraftComponentMixin.js'
import { LocalizeController } from '../localization/LocalizeController.js'
import {
  DEFAULT_AUTO_THRESHOLD,
  formatFullTimestamp,
  formatTimestamp,
  getNextTimestampUpdateDelay,
  parseTimestamp,
  resolveTimestampFormat,
} from './_timestamp-helpers.js'
import style from './Timestamp.light-dom.css'
import '../tooltip/Tooltip.js'

export type { TimestampFormat } from './_timestamp-helpers.js'

export type TimestampSize = 'xs' | 's' | 'm' | 'l'
export type TimestampColor = 'default' | 'weak' | 'weaker' | 'weakest'
export type TimestampWeight = 'regular' | 'active' | 'heading' | 'strong'

const scopeKey = 'nord-timestamp'
const maximumTimeoutDelay = 2_147_483_647
let tooltipCounter = 0

/**
 * Timestamp presents an instant as localized, stable system, relative, or
 * automatically selected text while retaining native date-time semantics.
 *
 * @status draft
 * @category content
 * @lightDom true
 *
 * @cssprop [--n-timestamp-color=var(--n-color-text-weaker)] - Controls the timestamp text color.
 * @cssprop [--n-timestamp-font-size] - Controls the timestamp font size. By default it inherits from surrounding text.
 * @cssprop [--n-timestamp-font-weight] - Controls the timestamp font weight. By default it inherits from surrounding text.
 */
@customElement('nord-timestamp')
export default class Timestamp extends DraftComponentMixin(LitElement) {
  /**
   * The instant to display. Strings use Timestamp's documented ISO calendar
   * profile; finite numbers assigned through JavaScript are Unix seconds.
   */
  @property() value?: string | number

  /** The display format. Automatic format uses relative text near the instant. */
  @property({ reflect: true }) format: TimestampFormat = 'auto'

  /** Maximum age, in seconds, for relative output in automatic format. */
  @property({ attribute: 'auto-threshold', reflect: true, type: Number })
  autoThreshold = DEFAULT_AUTO_THRESHOLD

  /** Shows the full absolute timestamp in a Nord Tooltip for relative output. */
  @property({ attribute: 'show-tooltip', reflect: true, type: Boolean })
  showTooltip = false

  /** Adds a short timezone name to localized formats which include time. */
  @property({ attribute: 'show-time-zone', reflect: true, type: Boolean })
  showTimeZone = false

  /** IANA timezone used for formatting. Defaults to the runtime timezone. */
  @property({ attribute: 'time-zone', reflect: true }) timeZone?: string

  /** Updates relative and automatic output at meaningful display boundaries. */
  @property({ reflect: true, type: Boolean }) live = false

  /** Optional Nord text size. Omit it to inherit the surrounding font size. */
  @property({ reflect: true }) size?: TimestampSize

  /** Optional Nord text color. */
  @property({ reflect: true }) color?: TimestampColor

  /** Optional Nord text weight. Omit it to inherit the surrounding weight. */
  @property({ reflect: true }) weight?: TimestampWeight

  private readonly tooltipId = `n-timestamp-tooltip-${++tooltipCounter}`
  private readonly timeRef = createRef<HTMLTimeElement>()
  private readonly tooltipRef = createRef<Tooltip>()
  private referenceNow = new Date(0)
  private timeoutId?: ReturnType<typeof setTimeout>
  private visibilityDocument?: Document
  private localize = new LocalizeController<'nord-timestamp'>(this, {
    onLangChange: () => this.cancelLiveUpdate(),
  })

  connectedCallback() {
    super.connectedCallback()
    injectLightDomStyles(this, style, scopeKey)
    this.visibilityDocument = this.ownerDocument
    this.visibilityDocument.addEventListener('visibilitychange', this.handleVisibilityChange)
  }

  disconnectedCallback() {
    this.cancelLiveUpdate()
    this.visibilityDocument?.removeEventListener('visibilitychange', this.handleVisibilityChange)
    this.visibilityDocument = undefined
    super.disconnectedCallback()
  }

  protected createRenderRoot() {
    return this
  }

  protected willUpdate() {
    this.referenceNow = new Date(Date.now())
  }

  protected updated(_changedProperties: PropertyValues<this>) {
    const ownedElements = [this.timeRef.value, this.tooltipRef.value].filter(
      (element): element is HTMLTimeElement | Tooltip => element !== undefined,
    )
    setLightDomScope(scopeKey, ...ownedElements)
    this.scheduleLiveUpdate()
  }

  private handleVisibilityChange = () => {
    if (this.ownerDocument.visibilityState === 'hidden') {
      this.cancelLiveUpdate()
      return
    }
    this.requestUpdate()
  }

  private cancelLiveUpdate() {
    if (this.timeoutId !== undefined) {
      clearTimeout(this.timeoutId)
      this.timeoutId = undefined
    }
  }

  private scheduleLiveUpdate() {
    this.cancelLiveUpdate()
    if (!this.live || !this.isConnected || this.ownerDocument.visibilityState === 'hidden') {
      return
    }

    const date = parseTimestamp(this.value)
    if (!date) {
      return
    }

    const delay = getNextTimestampUpdateDelay(
      date,
      this.format,
      this.referenceNow,
      this.autoThreshold,
    )
    if (delay === undefined) {
      return
    }

    this.timeoutId = setTimeout(() => {
      this.timeoutId = undefined
      this.requestUpdate()
    }, Math.min(delay, maximumTimeoutDelay))
  }

  render() {
    const date = parseTimestamp(this.value)
    if (!date) {
      return nothing
    }

    const now = this.referenceNow
    const locale = this.localize.lang
    const resolvedFormat = resolveTimestampFormat(date, this.format, now, this.autoThreshold)
    const isRelative = resolvedFormat === 'relative'
    const fullTimestamp = isRelative
      ? formatFullTimestamp(date, { locale, timeZone: this.timeZone })
      : ''
    const hasTooltip = isRelative && this.showTooltip
    const text = formatTimestamp(date, this.format, {
      now,
      locale,
      timeZone: this.timeZone,
      showTimeZone: this.showTimeZone,
      autoThreshold: this.autoThreshold,
    })

    return html`
      <time
        ${ref(this.timeRef)}
        class="n-timestamp-time"
        datetime=${date.toISOString()}
        aria-label=${isRelative ? (hasTooltip ? text : fullTimestamp) : nothing}
        aria-describedby=${hasTooltip ? this.tooltipId : nothing}
        tabindex=${hasTooltip ? '0' : nothing}
      >${text}</time>
      ${hasTooltip
        ? html`<nord-tooltip ${ref(this.tooltipRef)} id=${this.tooltipId}>${fullTimestamp}</nord-tooltip>`
        : nothing}
    `
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'nord-timestamp': Timestamp
  }
}
